import logger from '@/lib/logger'
import type { SessionWithData } from '@/types/auth'
import { IfcConfirmRequiredError, parseBackendErrorMessage, type IfcConfirmDetail } from '@/utils/errors'

type CrudMethods = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD'

type MakeRequestProps = {
    url: URL
    method?: CrudMethods
    headers?: HeadersInit
    next?: {
        revalidate?: number | false
        tags?: string[]
    }
    cache?: RequestCache
    timeout?: number
}

type MakeJSONRequestProps = MakeRequestProps & {
    body?: object | string | number | boolean
}

type MakeFormDataRequestProps = MakeRequestProps & {
    body?: FormData
}

const jsonRequest = async <T = void>(props: MakeJSONRequestProps): Promise<T> => {
    const {
        url,
        headers: inputHeaders,
        body,
        method = 'GET',
        timeout = parseInt(process.env.NEXT_PUBLIC_LATENCY_ABORT),
        next,
        ...otherProps
    } = props

    const headers: HeadersInit = inputHeaders
        ? inputHeaders
        : {
              'Content-type': 'application/json',
              accept: 'application/json',
          }
    await addAccessToken(headers)

    const request = new Request(url.toString(), {
        ...(timeout && { signal: AbortSignal.timeout(timeout) }),
        method,
        body: JSON.stringify(body),
        headers,
        next,
        ...otherProps,
    })
    const response = await fetch(request)
    return await finalizeRequest<T>(response)
}

const formDataRequest = async <T = void>(props: MakeFormDataRequestProps): Promise<T> => {
    const {
        url,
        headers: inputHeaders,
        body,
        timeout = parseInt(process.env.NEXT_PUBLIC_LATENCY_ABORT),
        next,
        method = 'GET',
        ...otherProps
    } = props

    const headers: HeadersInit = inputHeaders
        ? inputHeaders
        : {
              'Content-type': 'application/json',
              accept: 'application/json',
          }
    await addAccessToken(headers)

    const request = new Request(url.toString(), {
        ...(timeout && { signal: AbortSignal.timeout(timeout) }),
        method,
        body,
        headers,
        next,
        ...otherProps,
    })

    const response = await fetch(request)
    return await finalizeRequest<T>(response)
}

const finalizeRequest = async <T = void>(response: Response): Promise<T> => {
    if (!response.ok) {
        logger.dev(response.url)
        const text = await response.clone().text()

        // 409 "conferma richiesta" sulla mappatura IFC: lo trasformiamo in un errore TIPIZZATO col dettaglio,
        // così il form può mostrare l'avviso e rifare il salvataggio con confirmIfcOverwrite=true (invece di
        // finire in un generico toast di errore).
        if (response.status === 409) {
            try {
                const parsed = JSON.parse(text) as { errors?: IfcConfirmDetail; title?: string }
                if (parsed.errors?.code === 'IFC_CONFIRM_REQUIRED') {
                    throw new IfcConfirmRequiredError(parsed.errors, parsed.title ?? 'Conferma richiesta')
                }
            } catch (error) {
                if (error instanceof IfcConfirmRequiredError) throw error
                // body non-JSON o 409 non-IFC: prosegue con la gestione generica sotto.
            }
        }

        const errorMessages: string[] = ['API Error']

        if (response.status) {
            errorMessages.push(response.status.toString())
        }

        if (response.statusText) {
            errorMessages.push(response.statusText)
        }

        const backendError = parseBackendErrorMessage(text)

        if (backendError) {
            errorMessages.push(backendError)
        }

        const errorMessage = errorMessages.join(' | ')
        logger.serverError(errorMessage)
        throw new Error(errorMessage)
    }

    const contentType = response.headers.get('content-type')

    if (contentType?.includes('application/json')) {
        const json = (await response.json()) as T
        return json
    }

    if (contentType?.includes('text/html')) {
        const text = (await response.text()) as unknown as T
        return text
    }

    return null as T
}

const getRequest = async <T = void>(props: MakeJSONRequestProps): Promise<T> =>
    await jsonRequest<T>({ ...props, method: 'GET' })
const postJsonRequest = async <T = void>(props: MakeJSONRequestProps): Promise<T> =>
    await jsonRequest<T>({ ...props, method: 'POST' })
const putJsonRequest = async <T = void>(props: MakeJSONRequestProps): Promise<T> =>
    await jsonRequest<T>({ ...props, method: 'PUT' })
const patchJsonRequest = async <T = void>(props: MakeJSONRequestProps): Promise<T> =>
    await jsonRequest<T>({ ...props, method: 'PATCH' })
const postFormDataRequest = async <T = void>(props: MakeFormDataRequestProps): Promise<T> =>
    await formDataRequest<T>({ ...props, method: 'POST' })
const putFormDataRequest = async <T = void>(props: MakeFormDataRequestProps): Promise<T> =>
    await formDataRequest<T>({ ...props, method: 'PUT' })
const patchFormDataRequest = async <T = void>(props: MakeFormDataRequestProps): Promise<T> =>
    await formDataRequest<T>({ ...props, method: 'PATCH' })
const deleteJsonRequest = async <T = void>(props: MakeJSONRequestProps): Promise<T> =>
    await jsonRequest<T>({ ...props, method: 'DELETE' })
const deleteFormDataRequest = async <T = void>(props: MakeFormDataRequestProps): Promise<T> =>
    await jsonRequest<T>({ ...props, method: 'DELETE' })
const headRequest = async <T = void>(props: MakeJSONRequestProps): Promise<T> =>
    await jsonRequest<T>({ ...props, method: 'HEAD' })

const addAccessToken = async (headers: HeadersInit): Promise<void> => {
    if (typeof window === 'undefined') {
        const session = (await (await import('@/auth').then((nextAuth) => nextAuth.auth))()) as SessionWithData | null
        const accessToken = session?.accessToken
        if (accessToken) {
            const headersAsRecords = headers as Record<string, string>
            headersAsRecords.Authorization = `Bearer ${accessToken}`
        }
    }
}

export {
    deleteFormDataRequest,
    deleteJsonRequest,
    getRequest,
    headRequest,
    patchFormDataRequest,
    patchJsonRequest,
    postFormDataRequest,
    postJsonRequest,
    putFormDataRequest,
    putJsonRequest,
}
